import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  useTheme,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { formatRate, getBookieLogoUrl } from "../constants";
import ToggleableDate from "./ToggleableDate";

const MobileSGMCard = ({
  row,
  addToSGMBuilder,
  sgmSelections,
  handleGroupedBetClick,
  copyToBetTracker,
}) => {
  const theme = useTheme();

  // Check if this selection is already in the SGM Builder
  const selectionKey = `${row.market}:${row.key}:${row.bookie}`;
  const isSelected = sgmSelections.some(
    (selection) =>
      `${selection.market}:${selection.selection}:${selection.bookie}` ===
      selectionKey
  );

  return (
    <Box
      sx={{
        width: "100%",
        p: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        mb: 1,
        backgroundColor: theme.palette.background.paper,
        boxShadow: 1,
      }}
    >
      {/* Market and Selection */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 1.5,
          alignItems: "flex-start",
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5 }}>
            {row.market}
          </Typography>
          <Typography variant="body2">{row.key}</Typography>
        </Box>

        {/* SGM Add button */}
        <IconButton
          onClick={() => addToSGMBuilder(row)}
          size="small"
          color={isSelected ? "secondary" : "primary"}
          sx={{ ml: 1 }}
        >
          <AddCircleOutlineIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Bookie and Date */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              width: "80px",
              height: "25px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={() => window.open(row.source)}
          >
            <img
              src={getBookieLogoUrl(row.bookie)}
              alt={row.bookie}
              style={{
                width: "80px",
                height: "25px",
                objectFit: "contain",
                borderRadius: "4px",
              }}
            />
          </Box>
        </Box>
        <Box>
          <ToggleableDate date={row.eventStart} />
        </Box>
      </Box>

      {/* Odds row */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Odds
          </Typography>
          <Typography variant="body2" fontWeight="medium">
            {formatRate(row.rate || 0)}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary">
            Best Alt
          </Typography>
          <Typography variant="body2" fontWeight="medium">
            {formatRate(row.bestAltOdds || 0)}
          </Typography>
        </Box>

        <Box
          onClick={(e) => {
            if (row.bookieCount) {
              handleGroupedBetClick(row);
            }
          }}
          sx={{
            cursor: row.bookieCount ? "pointer" : "default",
            "&:hover": {
              textDecoration: row.bookieCount ? "underline" : "none",
            },
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Count
          </Typography>
          <Typography
            variant="body2"
            fontWeight="medium"
            sx={{ display: "flex", alignItems: "center" }}
          >
            {row.bookieCount || 0}
            {row.bookieCount > 0 && (
              <InfoOutlinedIcon
                sx={{
                  fontSize: 14,
                  opacity: 0.7,
                  color: "primary.main",
                  ml: 0.5,
                }}
              />
            )}
          </Typography>
        </Box>
      </Box>

      {/* EV and Fair Odds row */}
      {row.fairOdds > 0 && (
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Fair Odds
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {formatRate(row.fairOdds || 0, true)}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              EV
            </Typography>
            <Typography
              variant="body2"
              fontWeight="bold"
              sx={{
                color:
                  row.ev >= 3 ? "#35BD5B" : row.ev > 0 ? "#FFA500" : "#FF0000",
              }}
            >
              {row.ev !== undefined ? `${row.ev.toFixed(2)}%` : "N/A"}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Odds/Fair
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {row.oddsFairRatio
                ? `${((row.oddsFairRatio - 1) * 100).toFixed(2)}%`
                : "N/A"}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Odds ratios row */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Odds/Avg
          </Typography>
          <Typography variant="body2" fontWeight="medium">
            {row.oddsAveRatio
              ? `${((row.oddsAveRatio - 1) * 100).toFixed(2)}%`
              : "N/A"}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary">
            Odds/Alt
          </Typography>
          <Typography variant="body2" fontWeight="medium">
            {row.firstSecondRatio
              ? `${((row.firstSecondRatio - 1) * 100).toFixed(2)}%`
              : "N/A"}
          </Typography>
        </Box>
      </Box>

      {/* Copy button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          startIcon={<ContentCopyIcon fontSize="small" />}
          size="small"
          onClick={() => copyToBetTracker(row)}
          sx={{ mt: 1 }}
        >
          Copy to Bet Tracker
        </Button>
      </Box>
    </Box>
  );
};

export default MobileSGMCard;
